import { useMemo } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { getNestedDataFetcher } from '@/utils/apis';
import { BASE_CALENDAR_URL } from '@/constant/apiList';
import type { CalendarId, Day } from '@/types/api';

/** 백엔드 캘린더 목록에서 공휴일 캘린더를 식별하는 이름 */
const HOLIDAY_CALENDAR_KEYWORD = '대한민국 공휴일';

const SWR_OPTIONS = {
  revalidateOnFocus: false,
  dedupingInterval: 1000 * 60 * 60 * 24,
};

type HolidayEvent = {
  id: string;
  summary?: string;
  start: Day;
  end?: Day;
};

/**
 * 종일 일정은 KST 자정을 UTC 로 저장한다 (예: 2003-12-31T15:00Z = KST 2004-01-01).
 * 런타임 타임존과 무관하게 +9h 후 UTC 기준 날짜를 취해 'YYYY-MM-DD' 로 변환.
 */
const toKstDateKey = (iso: string): string => {
  const kst = new Date(Date.parse(iso) + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const d = String(kst.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** 이벤트 목록 → 'YYYY-MM-DD' 공휴일 Set. end 는 같은 날 23:59(포함)이라 inclusive 전개 */
const buildHolidaySet = (events?: HolidayEvent[]): Set<string> => {
  const set = new Set<string>();
  events?.forEach((ev) => {
    if (!ev.start?.datetime) return;
    const startKey = toKstDateKey(ev.start.datetime);
    const endKey = ev.end?.datetime ? toKstDateKey(ev.end.datetime) : startKey;
    let cursor = dayjs(startKey);
    const end = dayjs(endKey);
    while (!cursor.isAfter(end, 'day')) {
      set.add(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
    }
  });
  return set;
};

/**
 * 한국 공휴일 'YYYY-MM-DD' Set 을 백엔드 캘린더 API 에서 조회.
 * 1) GET /api/v1/calendar?keyword=대한민국 공휴일 → 공휴일 캘린더 id
 * 2) GET /api/v1/calendar/{id}/event?sortType=start.datetime&orderType=desc&listSize=500
 *
 * 주의(백엔드 실측 기준):
 * - startDatetime/endDatetime 날짜 필터는 무시되고, timezone 지정 시 0건이 와서 둘 다 사용하지 않는다.
 * - 대신 최신순(desc)으로 전체를 받아 클라이언트에서 Set 으로 만든다.
 * - 캘린더에 등록된 연도까지만 표시된다 (미등록 연도는 빈 값 → 표시·제외 없음).
 */
export const useHolidays = (): Set<string> => {
  const { data: calendars } = useSWR<CalendarId[]>(
    `${BASE_CALENDAR_URL}?keyword=${HOLIDAY_CALENDAR_KEYWORD}`,
    getNestedDataFetcher,
    SWR_OPTIONS,
  );

  const calendarId =
    calendars?.find((c) => c.summary === HOLIDAY_CALENDAR_KEYWORD)?.id ??
    calendars?.[0]?.id;

  const { data: events } = useSWR<HolidayEvent[]>(
    calendarId
      ? `${BASE_CALENDAR_URL}/${calendarId}/event?sortType=start.datetime&orderType=desc&listSize=500`
      : null,
    getNestedDataFetcher,
    SWR_OPTIONS,
  );

  return useMemo(() => buildHolidaySet(events), [events]);
};
